"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeMoneyIdentity = computeMoneyIdentity;
exports.sumMoneyIdentityInputs = sumMoneyIdentityInputs;
exports.sumMoneyIdentities = sumMoneyIdentities;
exports.isGr4IdentitySatisfied = isGr4IdentitySatisfied;
exports.assertGr4Identity = assertGr4Identity;
const round_money_js_1 = require("../money/round-money.js");
/** Max acceptable drift (in currency units) before GR4 is considered violated. */
const GR4_EPSILON = 0.01;
/**
 * Derives the GR4 money identity from independently-aggregated inputs.
 * `toBeCollected` and `inEscrow` are computed as remainders, so the identity
 * (contractValue = collected + inEscrow + toBeCollected) always holds by
 * construction for well-formed input — `assertGr4Identity` exists to catch
 * rounding drift or future refactors that break that invariant.
 */
function computeMoneyIdentity(input) {
    const contractValue = (0, round_money_js_1.roundMoney)(input.contractValue);
    const funded = (0, round_money_js_1.roundMoney)(input.funded);
    const collected = (0, round_money_js_1.roundMoney)(input.released);
    const inEscrow = (0, round_money_js_1.roundMoney)(funded - collected);
    const toBeCollected = (0, round_money_js_1.roundMoney)(contractValue - funded);
    return { contractValue, collected, inEscrow, toBeCollected };
}
/** Sums raw (pre-identity) inputs across rows, then derives one aggregate identity. */
function sumMoneyIdentityInputs(rows) {
    const totals = rows.reduce((acc, row) => ({
        contractValue: acc.contractValue + (Number(row.contractValue) || 0),
        funded: acc.funded + (Number(row.funded) || 0),
        released: acc.released + (Number(row.released) || 0),
    }), { contractValue: 0, funded: 0, released: 0 });
    return computeMoneyIdentity(totals);
}
/** Sums already-derived identities (e.g. per-project rows) into one aggregate identity. */
function sumMoneyIdentities(rows) {
    const totals = rows.reduce((acc, row) => ({
        contractValue: acc.contractValue + (Number(row.contractValue) || 0),
        collected: acc.collected + (Number(row.collected) || 0),
        inEscrow: acc.inEscrow + (Number(row.inEscrow) || 0),
        toBeCollected: acc.toBeCollected + (Number(row.toBeCollected) || 0),
    }), { contractValue: 0, collected: 0, inEscrow: 0, toBeCollected: 0 });
    return {
        contractValue: (0, round_money_js_1.roundMoney)(totals.contractValue),
        collected: (0, round_money_js_1.roundMoney)(totals.collected),
        inEscrow: (0, round_money_js_1.roundMoney)(totals.inEscrow),
        toBeCollected: (0, round_money_js_1.roundMoney)(totals.toBeCollected),
    };
}
function isGr4IdentitySatisfied(identity, epsilon = GR4_EPSILON) {
    const sum = (0, round_money_js_1.roundMoney)(identity.collected + identity.inEscrow + identity.toBeCollected);
    return Math.abs(sum - (0, round_money_js_1.roundMoney)(identity.contractValue)) <= epsilon;
}
/** Throws if GR4 (contractValue = collected + inEscrow + toBeCollected) does not hold. */
function assertGr4Identity(identity, context = "money identity") {
    if (!isGr4IdentitySatisfied(identity)) {
        throw new Error(`GR4 violated for ${context}: contractValue(${identity.contractValue}) !== ` +
            `collected(${identity.collected}) + inEscrow(${identity.inEscrow}) + toBeCollected(${identity.toBeCollected})`);
    }
}
